import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  RequestStatus,
  RequestItemStatus,
  OffboardingAction,
  TaskPhase,
  ApprovalStatus,
} from "../../web-api-client";

/**
 * CVA variants for status badges with consistent styling across the app.
 * Uses subtle backgrounds with matching text colors for a professional look.
 */
const statusBadgeVariants = cva(
  "inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full border transition-colors",
  {
    variants: {
      variant: {
        // Neutral/Draft
        gray: "bg-gray-100 text-gray-800 border-gray-300",
        // Pending/Warning states
        yellow: "bg-yellow-100 text-yellow-800 border-yellow-400",
        amber: "bg-amber-100 text-amber-800 border-amber-400",
        // In Progress/Info states
        blue: "bg-blue-100 text-blue-800 border-blue-500",
        // Success/Completed states
        green: "bg-green-100 text-green-800 border-green-500",
        // Error/Rejected states
        red: "bg-red-100 text-red-800 border-red-400",
        // Special states
        teal: "bg-teal-100 text-teal-800 border-teal-400",
        purple: "bg-purple-100 text-purple-800 border-purple-400",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        md: "px-2.5 py-0.5 text-xs",
        lg: "px-3 py-1.5 text-sm font-semibold shadow-sm",
      },
    },
    defaultVariants: {
      variant: "gray",
      size: "md",
    },
  }
);

// ============================================
// Request Status Badge
// ============================================

const requestStatusConfig: Record<RequestStatus, { variant: VariantProps<typeof statusBadgeVariants>["variant"]; label: string }> = {
  [RequestStatus.Draft]: { variant: "gray", label: "Draft" },
  [RequestStatus.Pending]: { variant: "yellow", label: "Pending" },
  [RequestStatus.PendingFirstApproval]: { variant: "yellow", label: "Pending 1st Approval" },
  [RequestStatus.PendingSecondApproval]: { variant: "yellow", label: "Pending 2nd Approval" },
  [RequestStatus.Approved]: { variant: "blue", label: "Approved" },
  [RequestStatus.InProgress]: { variant: "blue", label: "In Progress" },
  [RequestStatus.Completed]: { variant: "green", label: "Completed" },
  [RequestStatus.Rejected]: { variant: "red", label: "Rejected" },
  [RequestStatus.Cancelled]: { variant: "red", label: "Cancelled" },
};

// ============================================
// Request Item Status Badge
// ============================================

const requestItemStatusConfig: Record<RequestItemStatus, { variant: VariantProps<typeof statusBadgeVariants>["variant"]; label: string }> = {
  [RequestItemStatus.Pending]: { variant: "yellow", label: "Pending" },
  [RequestItemStatus.Completed]: { variant: "green", label: "Completed" },
  [RequestItemStatus.Rejected]: { variant: "red", label: "Rejected" },
  [RequestItemStatus.Returned]: { variant: "teal", label: "Returned" },
};

// ============================================
// Offboarding Action Badge
// ============================================

const offboardingActionConfig: Record<OffboardingAction, { variant: VariantProps<typeof statusBadgeVariants>["variant"]; label: string }> = {
  [OffboardingAction.Outstanding]: { variant: "yellow", label: "Outstanding" },
  [OffboardingAction.Revoked]: { variant: "green", label: "Revoked" },
  [OffboardingAction.Returned]: { variant: "green", label: "Returned" },
  [OffboardingAction.Extended]: { variant: "blue", label: "Extended" },
};

// ============================================
// Task Phase Badge
// ============================================

const taskPhaseConfig: Record<TaskPhase, { variant: VariantProps<typeof statusBadgeVariants>["variant"]; label: string }> = {
  [TaskPhase.Onboarding]: { variant: "purple", label: "Onboarding" },
  [TaskPhase.PostOnboarding]: { variant: "blue", label: "Post-Onboarding" },
};

// ============================================
// Approval Status Badge
// ============================================

const approvalStatusConfig: Record<ApprovalStatus, { variant: VariantProps<typeof statusBadgeVariants>["variant"]; label: string }> = {
  [ApprovalStatus.Pending]: { variant: "amber", label: "Pending Approval" },
  [ApprovalStatus.Approved]: { variant: "green", label: "Approved" },
  [ApprovalStatus.Rejected]: { variant: "red", label: "Rejected" },
  [ApprovalStatus.Expired]: { variant: "gray", label: "Expired" },
};

// ============================================
// Type definitions
// ============================================

type StatusType = "requestStatus" | "itemStatus" | "offboardingAction" | "taskPhase" | "approvalStatus";

type StatusValueMap = {
  requestStatus: RequestStatus;
  itemStatus: RequestItemStatus;
  offboardingAction: OffboardingAction;
  taskPhase: TaskPhase;
  approvalStatus: ApprovalStatus;
};

interface StatusBadgeProps<T extends StatusType> extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  type: T;
  value: StatusValueMap[T] | undefined | null;
  size?: VariantProps<typeof statusBadgeVariants>["size"];
  icon?: React.ReactNode;
  showLabel?: boolean;
}

// ============================================
// Helper functions
// ============================================

function getConfig<T extends StatusType>(
  type: T,
  value: StatusValueMap[T] | undefined | null
): { variant: VariantProps<typeof statusBadgeVariants>["variant"]; label: string } {
  const defaultConfig = { variant: "gray" as const, label: "Unknown" };

  if (value === undefined || value === null) {
    return defaultConfig;
  }

  switch (type) {
    case "requestStatus":
      return requestStatusConfig[value as RequestStatus] ?? defaultConfig;
    case "itemStatus":
      return requestItemStatusConfig[value as RequestItemStatus] ?? defaultConfig;
    case "offboardingAction":
      return offboardingActionConfig[value as OffboardingAction] ?? defaultConfig;
    case "taskPhase":
      return taskPhaseConfig[value as TaskPhase] ?? defaultConfig;
    case "approvalStatus":
      return approvalStatusConfig[value as ApprovalStatus] ?? defaultConfig;
    default:
      return defaultConfig;
  }
}

// ============================================
// Component
// ============================================

/**
 * StatusBadge - A type-safe, reusable status badge component
 *
 * @example
 * // Request status
 * <StatusBadge type="requestStatus" value={RequestStatus.Completed} />
 *
 * // Item status with custom size
 * <StatusBadge type="itemStatus" value={RequestItemStatus.Pending} size="lg" />
 *
 * // Offboarding action with icon
 * <StatusBadge type="offboardingAction" value={OffboardingAction.Revoked} icon={<CheckIcon />} />
 *
 * // Task phase
 * <StatusBadge type="taskPhase" value={TaskPhase.PostOnboarding} />
 */
function StatusBadge<T extends StatusType>({
  type,
  value,
  size,
  icon,
  showLabel = true,
  className,
  ...props
}: StatusBadgeProps<T>) {
  const config = getConfig(type, value);

  return (
    <span
      className={cn(statusBadgeVariants({ variant: config.variant, size }), className)}
      {...props}
    >
      {icon}
      {showLabel && config.label}
    </span>
  );
}

// ============================================
// Convenience Components
// ============================================

interface RequestStatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  status: RequestStatus | undefined | null;
  size?: VariantProps<typeof statusBadgeVariants>["size"];
  icon?: React.ReactNode;
}

/**
 * Convenience component for request status badges
 */
function RequestStatusBadge({ status, ...props }: RequestStatusBadgeProps) {
  return <StatusBadge type="requestStatus" value={status} {...props} />;
}

interface ItemStatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  status: RequestItemStatus | undefined | null;
  size?: VariantProps<typeof statusBadgeVariants>["size"];
  icon?: React.ReactNode;
}

/**
 * Convenience component for request item status badges
 */
function ItemStatusBadge({ status, ...props }: ItemStatusBadgeProps) {
  return <StatusBadge type="itemStatus" value={status} {...props} />;
}

interface OffboardingActionBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  action: OffboardingAction | undefined | null;
  size?: VariantProps<typeof statusBadgeVariants>["size"];
  icon?: React.ReactNode;
}

/**
 * Convenience component for offboarding action badges
 */
function OffboardingActionBadge({ action, ...props }: OffboardingActionBadgeProps) {
  return <StatusBadge type="offboardingAction" value={action} {...props} />;
}

interface TaskPhaseBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  phase: TaskPhase | undefined | null;
  size?: VariantProps<typeof statusBadgeVariants>["size"];
  icon?: React.ReactNode;
}

/**
 * Convenience component for task phase badges
 */
function TaskPhaseBadge({ phase, ...props }: TaskPhaseBadgeProps) {
  return <StatusBadge type="taskPhase" value={phase} {...props} />;
}

interface ApprovalStatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  status: ApprovalStatus | undefined | null;
  size?: VariantProps<typeof statusBadgeVariants>["size"];
  icon?: React.ReactNode;
}

/**
 * Convenience component for approval status badges
 */
function ApprovalStatusBadge({ status, ...props }: ApprovalStatusBadgeProps) {
  return <StatusBadge type="approvalStatus" value={status} {...props} />;
}

export {
  StatusBadge,
  RequestStatusBadge,
  ItemStatusBadge,
  OffboardingActionBadge,
  TaskPhaseBadge,
  ApprovalStatusBadge,
  statusBadgeVariants,
  // Export configs for external use if needed
  requestStatusConfig,
  requestItemStatusConfig,
  offboardingActionConfig,
  taskPhaseConfig,
  approvalStatusConfig,
};

export type { StatusBadgeProps, StatusType, StatusValueMap };
